import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { GripVertical, Loader2, X } from 'lucide-react';

type CoachOrderItem = {
    coachName: string;
    coachId?: string;
    id?: string;
    [key: string]: any;
};

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(18, 29, 49, 0.48);
    backdrop-filter: blur(2px);
`;

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: min(500px, calc(100vw - 32px));
    max-height: min(760px, calc(100vh - 40px));
    overflow: hidden;
    border: 1px solid #e2e8f1;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 24px 64px rgba(18, 37, 69, 0.24);
`;

const ModalHeader = styled.header`
    display: flex;
    flex: none;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid #edf1f6;
`;

const HeaderCopy = styled.div`
    min-width: 0;
    flex: 1;

    h2 {
        margin: 0;
        color: #1f2d43;
        font-size: 19px;
        font-weight: 800;
        line-height: 1.35;
        letter-spacing: -0.02em;
    }

    p {
        margin: 5px 0 0;
        color: #718096;
        font-size: 12.5px;
        font-weight: 500;
        line-height: 1.45;
    }
`;

const CoachCount = styled.span`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 28px;
    margin-top: 1px;
    padding: 0 10px;
    border-radius: 999px;
    color: #3568ca;
    background: #edf4ff;
    font-size: 12px;
    font-weight: 800;
`;

const CloseButton = styled.button`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 9px;
    color: #778399;
    background: transparent;
    cursor: pointer;

    &:hover:not(:disabled) {
        color: #34445d;
        background: #f2f5f9;
    }

    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 2px;
    }

    &:disabled { cursor: default; opacity: 0.45; }
`;

const CoachList = styled.div`
    min-height: 104px;
    padding: 8px 12px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
        border: 2px solid #fff;
        border-radius: 999px;
        background: #c9d2df;
    }
    &::-webkit-scrollbar-thumb:hover { background: #aeb9c8; }
`;

const CoachItem = styled.div<{ $isDragging?: boolean; $isAnyDragging?: boolean; $isSaving?: boolean }>`
    position: relative;
    z-index: ${(props) => (props.$isDragging ? 2 : 1)};
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) 42px;
    align-items: center;
    min-height: 46px;
    margin: 4px 0;
    padding: 0 6px 0 8px;
    border: 1px solid ${(props) => (props.$isDragging ? '#7ca5ed' : '#e3e9f1')};
    border-radius: 10px;
    background: ${(props) => (props.$isDragging ? '#edf4ff' : '#fff')};
    box-shadow: ${(props) => (props.$isDragging ? '0 8px 20px rgba(48, 92, 172, 0.18)' : 'none')};
    opacity: ${(props) => (props.$isSaving ? 0.68 : 1)};
    transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;

    &:hover {
        border-color: ${(props) => (props.$isAnyDragging || props.$isSaving ? '#e3e9f1' : '#b9ccec')};
        background: ${(props) => (props.$isAnyDragging || props.$isSaving ? '#fff' : '#f8faff')};
    }
`;

const Rank = styled.span`
    color: #8a96a8;
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    text-align: center;
`;

const CoachName = styled.span`
    min-width: 0;
    overflow: hidden;
    color: #27364f;
    font-size: 14px;
    font-weight: 750;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DragHandle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: #7c8aa0;
    background: transparent;
    touch-action: none;
    cursor: grab;

    &:hover:not(:disabled) {
        color: #3568ca;
        background: #edf4ff;
    }

    &:active:not(:disabled) { cursor: grabbing; }
    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 1px;
    }
    &:disabled { cursor: default; }
`;

const EmptyState = styled.div`
    display: flex;
    min-height: 120px;
    align-items: center;
    justify-content: center;
    color: #7c899c;
    font-size: 13px;
`;

const ModalFooter = styled.footer`
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
    padding: 14px 18px 16px;
    border-top: 1px solid #edf1f6;
    background: #fff;
`;

const FooterNote = styled.span<{ $changed: boolean }>`
    min-width: 0;
    flex: 1;
    color: ${(props) => (props.$changed ? '#3568ca' : '#7d899a')};
    font-size: 11.5px;
    font-weight: ${(props) => (props.$changed ? 700 : 500)};
    line-height: 1.4;
`;

const ButtonGroup = styled.div`
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 8px;
`;

const Button = styled.button<{ $variant: 'cancel' | 'apply' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${(props) => (props.$variant === 'apply' ? '94px' : '66px')};
    height: 38px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$variant === 'apply' ? '#3d73dd' : '#d8e0eb')};
    border-radius: 9px;
    color: ${(props) => (props.$variant === 'apply' ? '#fff' : '#4e5d72')};
    background: ${(props) => (props.$variant === 'apply' ? '#3d73dd' : '#fff')};
    font-size: 12.5px;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${(props) => (props.$variant === 'apply' ? '#315fba' : '#bdc8d7')};
        background: ${(props) => (props.$variant === 'apply' ? '#315fba' : '#f7f9fc')};
    }

    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 2px;
    }

    &:disabled {
        border-color: #e3e8ef;
        color: #a3adbb;
        background: #f3f5f8;
        cursor: default;
    }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const SavingIcon = styled(Loader2)`
    margin-right: 6px;
    animation: ${spin} 0.8s linear infinite;
`;

interface OrderChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    coaches: CoachOrderItem[];
    onApply: (reorderedCoaches: CoachOrderItem[]) => void;
    isSaving?: boolean;
}

interface SortableItemProps {
    coach: CoachOrderItem;
    id: string;
    index: number;
    isAnyDragging: boolean;
    isSaving: boolean;
}

const getCoachSortableId = (coach: CoachOrderItem) =>
    String(coach.coachId || coach.id || coach.coachName);

const SortableItem: React.FC<SortableItemProps> = ({
    coach,
    id,
    index,
    isAnyDragging,
    isSaving,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: isSaving,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? 'none' : transition,
    };

    return (
        <CoachItem
            ref={setNodeRef}
            style={style}
            $isDragging={isDragging}
            $isAnyDragging={isAnyDragging}
            $isSaving={isSaving}
        >
            <Rank aria-hidden="true">{String(index + 1).padStart(2, '0')}</Rank>
            <CoachName title={coach.coachName}>{coach.coachName}</CoachName>
            <DragHandle
                type="button"
                disabled={isSaving}
                aria-label={`${coach.coachName} 순서 이동, 현재 ${index + 1}번째`}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={20} aria-hidden="true" />
            </DragHandle>
        </CoachItem>
    );
};

const OrderChangeModal: React.FC<OrderChangeModalProps> = ({
    isOpen,
    onClose,
    coaches,
    onApply,
    isSaving = false,
}) => {
    const [reorderedCoaches, setReorderedCoaches] = useState(coaches);
    const [isAnyDragging, setIsAnyDragging] = useState(false);

    useEffect(() => {
        setReorderedCoaches(coaches);
    }, [coaches]);

    const originalOrder = useMemo(() => coaches.map(getCoachSortableId), [coaches]);
    const currentOrder = useMemo(() => reorderedCoaches.map(getCoachSortableId), [reorderedCoaches]);
    const hasChanges =
        originalOrder.length === currentOrder.length &&
        originalOrder.some((coachId, index) => coachId !== currentOrder[index]);

    useEffect(() => {
        if (!isOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isSaving && !hasChanges) onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [hasChanges, isOpen, isSaving, onClose]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setIsAnyDragging(false);
        if (!over || active.id === over.id) return;

        setReorderedCoaches((items) => {
            const oldIndex = items.findIndex((item) => getCoachSortableId(item) === active.id);
            const newIndex = items.findIndex((item) => getCoachSortableId(item) === over.id);
            if (oldIndex < 0 || newIndex < 0) return items;
            return arrayMove(items, oldIndex, newIndex);
        });
    };

    const requestClose = () => {
        if (isSaving || hasChanges) return;
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onMouseDown={(event) => event.target === event.currentTarget && requestClose()}>
            <ModalContainer
                role="dialog"
                aria-modal="true"
                aria-labelledby="coach-order-title"
                aria-describedby="coach-order-description"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <ModalHeader>
                    <HeaderCopy>
                        <h2 id="coach-order-title">강사 표시 순서</h2>
                        <p id="coach-order-description">드래그한 순서가 한눈에·기존 캘린더에 함께 적용돼요.</p>
                    </HeaderCopy>
                    <CoachCount>{coaches.length}명</CoachCount>
                    <CloseButton type="button" onClick={requestClose} disabled={isSaving || hasChanges} aria-label="닫기">
                        <X size={19} aria-hidden="true" />
                    </CloseButton>
                </ModalHeader>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                    onDragStart={() => setIsAnyDragging(true)}
                    onDragCancel={() => setIsAnyDragging(false)}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
                        <CoachList>
                            {reorderedCoaches.length === 0 ? (
                                <EmptyState>표시할 강사가 없습니다.</EmptyState>
                            ) : (
                                reorderedCoaches.map((coach, index) => {
                                    const sortableId = getCoachSortableId(coach);
                                    return (
                                        <SortableItem
                                            key={sortableId}
                                            id={sortableId}
                                            coach={coach}
                                            index={index}
                                            isAnyDragging={isAnyDragging}
                                            isSaving={isSaving}
                                        />
                                    );
                                })
                            )}
                        </CoachList>
                    </SortableContext>
                </DndContext>

                <ModalFooter>
                    <FooterNote $changed={hasChanges}>
                        {hasChanges ? '변경된 순서를 저장해 주세요.' : '오른쪽 핸들을 잡고 위아래로 이동하세요.'}
                    </FooterNote>
                    <ButtonGroup>
                        <Button type="button" $variant="cancel" onClick={onClose} disabled={isSaving}>
                            취소
                        </Button>
                        <Button
                            type="button"
                            $variant="apply"
                            onClick={() => onApply(reorderedCoaches)}
                            disabled={!hasChanges || isSaving || reorderedCoaches.length === 0}
                        >
                            {isSaving && <SavingIcon size={15} aria-hidden="true" />}
                            {isSaving ? '저장 중' : '순서 저장'}
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default OrderChangeModal;
